/**
 * GroundShadowManager — State + logic for ground shadow rendering.
 *
 * Three shadow modes:
 *   contact — Drei <ContactShadows> projected from below  (reacts to animations)
 *   baked   — Static PNG on a MeshBasicMaterial plane     (best performance)
 *   off     — No ground shadow
 *
 * `shadowLength` maps to ContactShadows `far` and is automatically derived from
 * the sun elevation — low elevation → long shadow, high elevation → short shadow.
 * SunlightManager calls applySunElevation() on every elevation change.
 *
 * @see docs/ims-engine-api/11-feature-lighting-shadow.md
 */

import * as THREE from 'three';
import type { ShadowMode, ShadowConfig } from '../api/types';

// ---------------------------------------------------------------------------
// Types (internal)
// ---------------------------------------------------------------------------

/** Full state snapshot for the R3F component to consume. */
export interface ShadowState {
  mode: ShadowMode;
  opacity: number;
  blur: number;
  /** Far distance for ContactShadows — auto-derived from sun elevation. */
  shadowLength: number;
  textureUrl: string | undefined;
  /** Number of frames ContactShadows renders (1 = static, Infinity = animated). */
  frames: number;
  /** Width multiplier applied to model AABB.x for the texture shadow plane. */
  m1: number;
  /** Depth multiplier applied to model AABB.z for the texture shadow plane. */
  m2: number;
  /** Y offset from model AABB bottom edge (world units) to prevent z-fighting. */
  yOffset: number;  /**
   * X offset of the baked shadow plane centre in world units (positive = right).
   * Does NOT affect contact shadows — only the baked/both plane.
   * Default: 0.
   */
  xOffset: number;
  /**
   * Z offset of the baked shadow plane centre in world units (positive = towards camera).
   * Does NOT affect contact shadows — only the baked/both plane.
   * Default: 0.
   */
  zOffset: number;  /** Axis-aligned bounding box of the loaded model — set once in onMounted. */
  modelAabb: THREE.Box3 | null;
}

// ---------------------------------------------------------------------------
// GroundShadowManager
// ---------------------------------------------------------------------------

export class GroundShadowManager {
  private _mode:         ShadowMode = 'contact';
  private _opacity:      number     = 0.95;
  private _blur:         number     = 1.8;
  private _shadowLength: number     = 3;
  private _textureUrl:   string | undefined = '/assets/textures/ground-shadow.png';
  private _frames:       number     = 1;
  private _m1:           number     = 1.35; // length
  private _m2:           number     = 2.25; // width
  private _yOffset:      number     = 0.004;
  private _xOffset:      number     = -0.33;
  private _zOffset:      number     = -0.22;
  private _modelAabb:    THREE.Box3 | null = null;

  /**
   * Called whenever the shadow state changes (mode, opacity, length…).
   * The R3F component subscribes here to trigger re-renders.
   */
  onChange?: (state: ShadowState) => void;

  // ---------------------------------------------------------------------------
  // apply
  // ---------------------------------------------------------------------------

  /**
   * Apply a shadow mode string or direct config.
   *
   * @example
   * manager.apply('contact')
   * manager.apply({ mode: 'baked', textureUrl: '/shadows/car.png', opacity: 0.55 })
   */
  apply(config: ShadowMode | ShadowConfig): void {
    if (typeof config === 'string') {
      this._mode = config;
    } else {
      if (config.mode       !== undefined) this._mode       = config.mode;
      if (config.opacity    !== undefined) this._opacity    = config.opacity;
      if (config.blur       !== undefined) this._blur       = config.blur;
      // null = explicitly force procedural fallback; undefined = leave unchanged
      if (config.textureUrl !== undefined) this._textureUrl = config.textureUrl ?? undefined;
      if (config.frames     !== undefined) this._frames     = config.frames;
      if (config.m1         !== undefined) this._m1         = config.m1;
      if (config.m2         !== undefined) this._m2         = config.m2;
      if (config.yOffset    !== undefined) this._yOffset    = config.yOffset;
      if (config.xOffset    !== undefined) this._xOffset    = config.xOffset;
      if (config.zOffset    !== undefined) this._zOffset    = config.zOffset;
    }
    this._notify();
  }

  // ---------------------------------------------------------------------------
  // Model AABB
  // ---------------------------------------------------------------------------

  /**
   * Store the loaded model's axis-aligned bounding box.
   * Called once from `onMounted` in createEngine so the texture-shadow plane
   * can be correctly sized and positioned.
   */
  setModelAabb(aabb: THREE.Box3): void {
    this._modelAabb = aabb.clone();
    this._notify();
  }

  // Sun elevation reaction
  // ---------------------------------------------------------------------------

  /**
   * Recompute shadow length from sun elevation.
   * Called by SunlightManager.onElevationChange.
   *
   * elevation: 0°  (horizon) → shadowLength ≈ 7   (very long)
   * elevation: 90° (zenith)  → shadowLength ≈ 1.5 (short, underfoot)
   */
  applySunElevation(elevation: number): void {
    // Clamp elevation to 0–90 for the lerp; angles above 90 (animation wrap)
    // are treated as 90° (sun is past zenith — shadow shortens then)
    const clamped = THREE.MathUtils.clamp(Math.abs(elevation % 180), 0, 90);
    const t = clamped / 90; // 0 = horizon, 1 = zenith
    const newLength = THREE.MathUtils.lerp(7, 1.5, t);

    if (Math.abs(newLength - this._shadowLength) < 0.01) return; // debounce tiny updates

    this._shadowLength = newLength;
    this._notify();
  }

  // ---------------------------------------------------------------------------
  // Getters / state
  // ---------------------------------------------------------------------------

  getState(): ShadowState {
    return {
      mode:         this._mode,
      opacity:      this._opacity,
      blur:         this._blur,
      shadowLength: this._shadowLength,
      textureUrl:   this._textureUrl,
      frames:       this._frames,
      m1:           this._m1,
      m2:           this._m2,
      yOffset:      this._yOffset,
      xOffset:      this._xOffset,
      zOffset:      this._zOffset,
      modelAabb:    this._modelAabb,
    };
  }

  get mode():         ShadowMode          { return this._mode; }
  get opacity():      number              { return this._opacity; }
  get blur():         number              { return this._blur; }
  get shadowLength(): number              { return this._shadowLength; }
  get textureUrl():   string | undefined  { return this._textureUrl; }
  get frames():       number              { return this._frames; }
  get m1():           number              { return this._m1; }
  get m2():           number              { return this._m2; }
  get yOffset():      number              { return this._yOffset; }
  get xOffset():      number              { return this._xOffset; }
  get zOffset():      number              { return this._zOffset; }
  get modelAabb():    THREE.Box3 | null   { return this._modelAabb; }

  // ---------------------------------------------------------------------------
  // dispose
  // ---------------------------------------------------------------------------

  dispose(): void {
    this.onChange = undefined;
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private _notify(): void {
    this.onChange?.(this.getState());
  }
}
